/**
 * /ninety/meetings — Meetings list + "Start Level-10" picker (F58 Phase 3).
 *
 * INDEX route (`meetings/`) — the run view `$id` must be a SIBLING, not a
 * child of this page: as a child it needed an <Outlet /> here and rendered
 * nothing (URL changed, list stayed — the "live meeting does not open" bug).
 *
 * Lists meetings for the teams the signed-in user belongs to (admins see all),
 * grouped live / scheduled / concluded. Starting a meeting picks a team (from
 * the user's teams) + a template, creates AND STARTS the meeting via the
 * service-role /api/meetings endpoints, and lands in the full-screen staged
 * run view already live on the Opening stage — no second Start interstitial
 * (owner feedback: clicking Start Level-10 must open the meeting, full screen).
 *
 * Access = team membership (DECISIONS FROM TIM): the team picker only offers the
 * teams the user is a member of; the server re-checks membership on create.
 */

import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardBody, StatusPill, type StatusKind } from '~/components/ui';
import { useAuth } from '~/lib/auth';
import { listMeetings, createMeeting, startMeeting, type Meeting } from '~/lib/api/meetings';
import { listTeams, type Team } from '~/lib/api/teams';

export const Route = createFileRoute('/_app/ninety/meetings/')({
  component: MeetingsPage,
});

const STATUS_KIND: Record<Meeting['status'], StatusKind> = {
  scheduled: 'pending', in_progress: 'info', concluded: 'completed',
};
const STATUS_LABEL: Record<Meeting['status'], string> = {
  scheduled: 'Scheduled', in_progress: 'Live', concluded: 'Concluded',
};

function MeetingsPage() {
  const auth = useAuth();
  const uid = auth.user?.id;
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['meetings'],
    queryFn: () => listMeetings(),
  });
  const { data: teamData } = useQuery({ queryKey: ['teams_with_members'], queryFn: () => listTeams() });

  // Teams the user can run a meeting for: their memberships (admins → all teams).
  const myTeams: Team[] = useMemo(() => {
    const teams = teamData?.teams ?? [];
    if (auth.hasRole('admin')) return teams;
    const mine = new Set((teamData?.members ?? []).filter((m) => m.user_id === uid).map((m) => m.team_id));
    return teams.filter((t) => mine.has(t.id));
  }, [teamData, auth, uid]);

  const teamName = useMemo(() => {
    const m: Record<string, string> = {};
    for (const t of teamData?.teams ?? []) m[t.id] = t.name;
    return m;
  }, [teamData]);

  const [teamId, setTeamId] = useState('');
  const create = useMutation({
    // Create AND start in one motion — the run view opens already live on the
    // Opening stage. (The run view also auto-starts as a belt-and-suspenders
    // for any scheduled meeting reached directly.)
    mutationFn: async (tid: string) => {
      const res = await createMeeting({ team_id: tid });
      await startMeeting(res.meeting.id).catch(() => { /* run view auto-starts */ });
      return res;
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
      navigate({ to: '/ninety/meetings/$id', params: { id: res.meeting.id } });
    },
  });

  const meetings = data?.meetings ?? [];
  const live = meetings.filter((m) => m.status === 'in_progress');
  const scheduled = meetings.filter((m) => m.status === 'scheduled');
  const concluded = meetings.filter((m) => m.status === 'concluded');

  return (
    <div className="px-4 sm:px-6 py-6 space-y-6 max-w-4xl">
      <Card>
        <CardBody className="space-y-3">
          <h2 className="font-bold text-[var(--color-navy)]">Start a Level-10</h2>
          {myTeams.length === 0 ? (
            <p className="text-sm text-[var(--color-ink-muted)]">
              You're not on a team yet. Ask an admin to add you (Accountability &rarr; Teams).
            </p>
          ) : (
            <form
              className="flex flex-wrap gap-2 items-center"
              onSubmit={(e) => { e.preventDefault(); const t = teamId || myTeams[0].id; create.mutate(t); }}
            >
              <select
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                className="rounded-md border border-[var(--color-line)] px-3 py-2 text-sm"
                aria-label="Team"
              >
                {teamId === '' && <option value="">Select a team…</option>}
                {myTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <Button type="submit" variant="primary" size="sm" disabled={create.isPending || (myTeams.length > 1 && !teamId)}>
                Start Level-10
              </Button>
              {create.error && <span className="text-xs text-[var(--color-danger)]">{(create.error as Error).message}</span>}
            </form>
          )}
        </CardBody>
      </Card>

      {isLoading && <p className="text-sm text-[var(--color-ink-muted)]">Loading meetings…</p>}
      {error && <p className="text-sm text-[var(--color-danger)]">{(error as Error).message}</p>}

      <Section title="Live now" meetings={live} teamName={teamName} emptyHidden />
      <Section title="Scheduled" meetings={scheduled} teamName={teamName} />
      <Section title="Past meetings" meetings={concluded} teamName={teamName} />
    </div>
  );
}

function Section({
  title, meetings, teamName, emptyHidden,
}: {
  title: string;
  meetings: Meeting[];
  teamName: Record<string, string>;
  emptyHidden?: boolean;
}) {
  if (emptyHidden && meetings.length === 0) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-xs uppercase tracking-wide font-bold text-[var(--color-ink-soft)]">{title}</h3>
      {meetings.length === 0 ? (
        <p className="text-sm text-[var(--color-ink-muted)]">None.</p>
      ) : (
        <ul className="space-y-2">
          {meetings.map((m) => (
            <li key={m.id}>
              <Link
                to="/ninety/meetings/$id"
                params={{ id: m.id }}
                className="block rounded-lg border border-[var(--color-line)] p-3 bg-white hover:border-[var(--color-navy)]"
              >
                <div className="flex items-center gap-2">
                  <span className="flex-1 font-medium">{m.title ?? 'Level-10 Meeting'}</span>
                  <StatusPill kind={STATUS_KIND[m.status]}>{STATUS_LABEL[m.status]}</StatusPill>
                </div>
                <div className="text-[11px] text-[var(--color-ink-muted)] mt-1">
                  {m.team_id ? teamName[m.team_id] ?? 'Team' : 'No team'}
                  {typeof m.rating_avg === 'number' && ` · rated ${m.rating_avg}/10`}
                  {m.started_at && ` · ${new Date(m.started_at).toLocaleString()}`}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
